'use client'

import { hotspotPhaseAt, type HotspotPhase } from '@/lib/hotspotButton'
import { computeContentBox, isKeptAtVideoEnd, isVideoTransitionClosing, toHitBox, toRectBox, type Box } from '@/lib/hotspotGeometry'
import type { Hotspot } from '@/types/interactiveVideo'
import { FC, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import HotspotButtonVisual from './HotspotButtonVisual'

export type WideHotspotLayerProps = {
  hotspots: Hotspot[]
  videoRef: RefObject<HTMLVideoElement | null>
  onSelect: (hotspotId: string) => void | Promise<void>
  /** テナントの既定ボタン色（見えるボタンの「ブランドカラー」に使う・ADR-0024） */
  brandColor?: string | null
}

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number
  cancelVideoFrameCallback?: (handle: number) => void
}

/**
 * ワイド型の動画に重ねるクリック領域（ホットスポット・ADR-0022）と見えるボタン（ADR-0024）。
 *
 * 見た目（appearance）が無い領域は、ボタンが動画に焼き込まれているので当たり判定だけを置く。
 * 見た目がある領域は、`rect` の大きさでボタンを描く（文字や角丸もボタンの高さに比例させる）。
 *
 * - 位置は映像フレームを 0〜1 とした比率。`object-fit: contain` で黒帯が出ても、
 *   実際に映像が描かれている矩形を計算して、その上に同じ大きさの層を敷く。
 * - 出入りの判定は `timeupdate`（約250ms 間隔）だと遅れるので、再生中は
 *   `requestVideoFrameCallback`（非対応ブラウザは requestAnimationFrame）でフレームごとに見る。
 * - 並び順が前のものほど上に重ねる（重なった場所では前が押される）。
 * - スタイルは inline で持つ（埋め込み先の CSS に左右されないため・ADR-0016）。
 */
const WideHotspotLayer: FC<WideHotspotLayerProps> = ({ hotspots, videoRef, onSelect, brandColor }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 })
  const [activeKey, setActiveKey] = useState('')
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  // コンテナの大きさ（全画面・リサイズ・モーダルの開閉で変わる）
  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    const sync = () => setContainerSize({ width: element.clientWidth, height: element.clientHeight })
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // 映像の縦横（動画が切り替わると変わる）
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const sync = () => setVideoSize({ width: video.videoWidth, height: video.videoHeight })
    sync()
    video.addEventListener('loadedmetadata', sync)
    video.addEventListener('resize', sync)
    return () => {
      video.removeEventListener('loadedmetadata', sync)
      video.removeEventListener('resize', sync)
    }
  }, [videoRef])

  // いま出ている領域（と退場アニメーション中のボタン）の判定。出入りが変わったときだけ再描画する
  useEffect(() => {
    const video = videoRef.current as VideoWithFrameCallback | null
    if (!video) return

    const update = () => {
      const time = video.currentTime
      const key = hotspots
        .map((hotspot) => {
          // 最後まで出す領域（同じ動画内への移動・終了3秒前より前から出ている「別の動画へ」）は終了後もその場に残す
          const kept = isKeptAtVideoEnd(hotspot, hotspot.action.type, video.duration)
          const phase = hotspot.action.type === 'video_transition' && !kept && isVideoTransitionClosing(time, video.duration)
            ? 'hidden'
            : hotspotPhaseAt(hotspot, kept ? Math.min(time, hotspot.endTime) : time)
          return `${hotspot.id}:${phase}`
        })
        .filter((entry) => !entry.endsWith(':hidden'))
        .join(',')
      setActiveKey((prev) => (prev === key ? prev : key))
    }

    let frameHandle: number | null = null
    let usesVideoFrame = false
    const loop = () => {
      update()
      if (video.paused || video.ended) {
        frameHandle = null
        return
      }
      if (video.requestVideoFrameCallback) {
        usesVideoFrame = true
        frameHandle = video.requestVideoFrameCallback(loop)
      } else {
        frameHandle = requestAnimationFrame(loop)
      }
    }
    const startLoop = () => {
      if (frameHandle === null) loop()
    }

    update()
    startLoop()
    video.addEventListener('play', startLoop)
    video.addEventListener('seeked', update)
    video.addEventListener('timeupdate', update)
    video.addEventListener('loadedmetadata', update)
    video.addEventListener('ended', update)
    return () => {
      if (frameHandle !== null) {
        if (usesVideoFrame) video.cancelVideoFrameCallback?.(frameHandle)
        else cancelAnimationFrame(frameHandle)
      }
      video.removeEventListener('play', startLoop)
      video.removeEventListener('seeked', update)
      video.removeEventListener('timeupdate', update)
      video.removeEventListener('loadedmetadata', update)
      video.removeEventListener('ended', update)
    }
  }, [videoRef, hotspots])

  const content: Box = useMemo(
    () => computeContentBox(containerSize.width, containerSize.height, videoSize.width, videoSize.height),
    [containerSize, videoSize],
  )

  const phases = new Map(
    (activeKey ? activeKey.split(',') : []).map((entry) => {
      const separator = entry.lastIndexOf(':')
      return [entry.slice(0, separator), entry.slice(separator + 1) as HotspotPhase] as const
    }),
  )
  const activeHotspots = hotspots.filter((hotspot) => phases.has(hotspot.id))

  return (
    <div
      ref={containerRef}
      data-testid="wide-hotspot-layer"
      style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none' }}
    >
      {content.width > 0 && (
        <div
          style={{
            position: 'absolute',
            left: `${content.left}px`,
            top: `${content.top}px`,
            width: `${content.width}px`,
            height: `${content.height}px`,
          }}
        >
          {activeHotspots.map((hotspot, index) => {
            const box = toHitBox(hotspot.rect, content)
            const isFocused = focusedId === hotspot.id
            const exiting = phases.get(hotspot.id) === 'exiting'
            const zIndex = activeHotspots.length - index
            const focusRing = {
              outline: isFocused ? '3px solid rgba(255,255,255,0.95)' : 'none',
              outlineOffset: '2px',
              boxShadow: isFocused ? '0 0 0 5px rgba(0,0,0,0.35)' : 'none',
            }

            // 退場アニメーション中のボタンは絵だけ残し、押せないようにする
            if (exiting && hotspot.appearance) {
              const visual = toRectBox(hotspot.rect, content)
              return (
                <div
                  key={`${hotspot.id}-exit`}
                  style={{
                    position: 'absolute',
                    left: `${visual.left}px`,
                    top: `${visual.top}px`,
                    width: `${visual.width}px`,
                    height: `${visual.height}px`,
                    zIndex,
                  }}
                >
                  <HotspotButtonVisual
                    label={hotspot.label}
                    appearance={hotspot.appearance}
                    width={visual.width}
                    height={visual.height}
                    videoWidth={content.width}
                    brandColor={brandColor}
                    exiting
                  />
                </div>
              )
            }

            // 見えるボタンは rect ちょうどに描き、当たり判定（box）だけスマホ向けに広げる
            const visual = hotspot.appearance ? toRectBox(hotspot.rect, content) : null
            return (
              <button
                key={hotspot.id}
                type="button"
                data-hotspot-id={hotspot.id}
                aria-label={hotspot.label}
                onClick={(event) => {
                  // 動画のクリック（再生/停止）やモーダルの外側クリックへ伝えない
                  event.stopPropagation()
                  void onSelect(hotspot.id)
                }}
                onTouchEnd={(event) => event.stopPropagation()}
                onFocus={(event) => {
                  // キーボード操作のときだけ枠を出す（マウスで押したときは透明のまま）
                  if (event.currentTarget.matches(':focus-visible')) setFocusedId(hotspot.id)
                }}
                onBlur={() => setFocusedId(null)}
                onMouseEnter={() => setHoveredId(hotspot.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  position: 'absolute',
                  left: `${box.left}px`,
                  top: `${box.top}px`,
                  width: `${box.width}px`,
                  height: `${box.height}px`,
                  zIndex,
                  margin: 0,
                  padding: 0,
                  border: 'none',
                  borderRadius: '8px',
                  background: 'transparent',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  ...(visual ? {} : focusRing),
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {hotspot.appearance && visual && (
                  <span
                    style={{
                      position: 'absolute',
                      left: `${visual.left - box.left}px`,
                      top: `${visual.top - box.top}px`,
                      width: `${visual.width}px`,
                      height: `${visual.height}px`,
                      borderRadius: '8px',
                      ...focusRing,
                    }}
                  >
                    <HotspotButtonVisual
                      label={hotspot.label}
                      appearance={hotspot.appearance}
                      width={visual.width}
                      height={visual.height}
                      videoWidth={content.width}
                      brandColor={brandColor}
                      isHover={hoveredId === hotspot.id}
                      animate
                    />
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default WideHotspotLayer
